// Hermes use case — PlanTeam: o LÍDER raciocina e decide solo-vs-equipe.
//
// Squad Orchestrator (E1): planner em DOIS estágios (06-REVIEW #2):
//   1. planner.reason  — raciocínio LIVRE (markdown, SEM schema) no brain da
//      sessão; o reasoner pensa sem ser constrangido por JSON.
//   2. planner.structure — converte o raciocínio em JSON ESTRITO com
//      temperature 0 (determinismo do passo de estruturação); constrained
//      decoding/validateTeamPlan SÓ aqui.
//
// Contrato com a alucinação é fallback-first (D4): structure inválido 2× /
// chat falho / timeout / signal abortado → { mode:'solo' }. planTeam NUNCA
// lança e NUNCA devolve plano não-validado (passa por validateTeamPlan).
// A fachada pública `makePlanTeam({chat})` + assinatura `planTeam({text,
// brain,memoryBlock,cap,signal})` é PRESERVADA (callers não mudam).

import { parseTeamPlan, validateTeamPlan, TEAM_LIMITS } from '../domain/teamPlan.js';

export const PLAN_TIMEOUT_MS = 60_000;
export const STRUCTURE_TIMEOUT_MS = 45_000;
/** Temperatura do passo de estruturação — 0 = determinístico (06-REVIEW #5). */
export const STRUCTURE_TEMPERATURE = 0;

/** @type {Map<string, import('../domain/teamPlan.js').TeamPlan>} */
const planTeamCache = new Map();

function planCacheKey({ text, memoryBlock = '', cap }) {
  return `${String(text)}::${memoryBlock}::${cap}`;
}

/** Clear plan cache (one team turn / run scope). */
export function clearPlanTeamCache() {
  planTeamCache.clear();
}

export const invalidatePlanTeamCache = clearPlanTeamCache;

const SETUP_BAN =
  'NÃO crie passos de setup/clone/instalação de dependências/preparar ambiente: o repositório JÁ está no diretório de trabalho de cada operário. Cada frente é uma parte REAL da tarefa.';

/** Estágio 1 — raciocínio livre (sem JSON). */
export function buildReasonPrompt({ text, memoryBlock = '', cap = TEAM_LIMITS.capDefault } = {}) {
  const system = [
    'Você é o LÍDER de uma equipe de subagents de engenharia dentro do Apolo IDE.',
    'Pense em voz alta (markdown livre, SEM JSON) sobre como atacar a tarefa do usuário.',
    'Decida:',
    '- "solo" quando a tarefa é simples, sequencial ou cabe melhor num agente só (na dúvida, solo).',
    `- "equipe" quando há de 2 a ${cap} partes INDEPENDENTES e autocontidas que podem rodar em paralelo sem editar os mesmos arquivos.`,
    SETUP_BAN,
    'Esboce as frentes e o papel curto de cada operário (ex.: pesquisador, executor, revisor). NÃO responda em JSON ainda — só o raciocínio.',
  ].join('\n');
  const user = [memoryBlock ? `${memoryBlock}\n` : '', 'Tarefa do usuário:', text]
    .filter(Boolean)
    .join('\n');
  return { system, user };
}

/** Estágio 2 — estruturação determinística (JSON estrito a partir do raciocínio). */
export function buildStructurePrompt({ reasoning = '', text, cap = TEAM_LIMITS.capDefault } = {}) {
  const system = [
    'Você converte um raciocínio de planejamento em JSON ESTRITO. NÃO adicione nada além do JSON.',
    `Modo "team" só com 2 a ${cap} subtasks INDEPENDENTES e autocontidas (o operário não vê o chat — só o texto da subtask); senão "solo".`,
    SETUP_BAN,
    'FORMATO DA RESPOSTA — apenas este JSON, nada antes nem depois:',
    '{"mode":"solo"|"team","reasoning":"resumo curto","subtasks":[{"title":"...","task":"...","role":"..."}]}',
  ].join('\n');
  const user = [
    'Tarefa do usuário:',
    text,
    '',
    'Raciocínio do planejamento (converta FIELMENTE isto em JSON, sem inventar frentes novas):',
    reasoning || '(sem raciocínio — derive direto da tarefa)',
  ].join('\n');
  return { system, user };
}

// Compat: prompt single-stage legado (mantido p/ não quebrar consumidores).
// Reusa o formato do estágio de estruturação sem o raciocínio separado.
export function buildPlannerPrompt({ text, memoryBlock = '', cap = TEAM_LIMITS.capDefault } = {}) {
  const { system } = buildStructurePrompt({ reasoning: '', text, cap });
  const user = [memoryBlock ? `${memoryBlock}\n` : '', 'Tarefa do usuário:', text]
    .filter(Boolean)
    .join('\n');
  return { system, user };
}

const REPAIR_USER =
  'Sua resposta anterior não era JSON válido no formato pedido. Responda AGORA apenas o JSON do plano — sem prosa, sem markdown, sem fence.';

export function makePlanTeam({ chat } = {}) {
  /**
   * planTeam({ text, brain, structureBrain?, memoryBlock, cap, signal, onCall? })
   *   → { mode:'solo'|'team', reasoning, subtasks } (sempre validado, nunca lança)
   * onCall(info) — repassado ao chat por estágio com `stage` ('reason'|'structure')
   * pro squad_call_log (E2). structureBrain default = brain (mesmo modelo, temp 0).
   */
  return async function planTeam({
    text,
    brain,
    structureBrain,
    memoryBlock = '',
    cap = TEAM_LIMITS.capDefault,
    signal,
    onCall,
    onUsage,
  } = {}) {
    const capN = Math.min(Math.max(cap ?? TEAM_LIMITS.capDefault, TEAM_LIMITS.capMin), TEAM_LIMITS.capMax);
    const cacheKey = planCacheKey({ text, memoryBlock, cap: capN });
    const cached = planTeamCache.get(cacheKey);
    if (cached) return cached;

    const stageCall = (stage) =>
      typeof onCall === 'function' ? (info) => onCall({ ...info, stage }) : undefined;
    // E2-WS3-T01 — surface each leader stage's usage (best-effort) so runTeamTurn
    // can fold the PLAN tokens/cost into the team stats. Never throws.
    const reportUsage = (usage) => {
      if (usage && typeof onUsage === 'function') {
        try { onUsage(usage); } catch { /* observabilidade best-effort */ }
      }
    };

    // ── Estágio 1: raciocínio livre (brain da sessão, temp default) ──
    let reasoning = '';
    try {
      const r = buildReasonPrompt({ text, memoryBlock, cap: capN });
      const res = await chat({
        brain,
        system: r.system,
        user: r.user,
        signal,
        timeoutMs: PLAN_TIMEOUT_MS,
        onCall: stageCall('reason'),
      });
      if (res?.ok && typeof res.content === 'string') reasoning = res.content.trim();
      reportUsage(res?.usage);
    } catch {
      reasoning = ''; // reason falhou → estrutura deriva direto da tarefa (degrada, não trava)
    }
    if (signal?.aborted) return validateTeamPlan({ mode: 'solo' });

    // ── Estágio 2: estruturação determinística (temp 0) ──
    const sBrain = structureBrain || brain;
    async function structAttempt(extra) {
      const s = buildStructurePrompt({ reasoning, text, cap: capN });
      const res = await chat({
        brain: sBrain,
        system: s.system,
        user: extra ? `${s.user}\n\n${extra}` : s.user,
        signal,
        timeoutMs: STRUCTURE_TIMEOUT_MS,
        temperature: STRUCTURE_TEMPERATURE,
        onCall: stageCall('structure'),
      });
      reportUsage(res?.usage);
      if (!res?.ok || typeof res.content !== 'string') return null;
      return parseTeamPlan(res.content);
    }

    let raw = null;
    try {
      raw = await structAttempt();
      if (!raw && !signal?.aborted) raw = await structAttempt(REPAIR_USER);
    } catch {
      raw = null;
    }
    if (!raw) return validateTeamPlan({ mode: 'solo' });
    const plan = validateTeamPlan(raw, { cap: capN });
    if (plan.mode === 'team') planTeamCache.set(cacheKey, plan);
    return plan;
  };
}
